// React
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// Third-party
import { signOut as firebaseSignOut } from "firebase/auth";
import { Eye, EyeOff } from "lucide-react";

// Hooks & Contexts
import { useTranslation } from "@/hooks";
import {
  useAuth,
  callAuthRegister,
  callSendEmailVerification,
} from "@/contexts";
import type { RegisterAccountType } from "@/contexts";

// Lib & Config
import { auth } from "@/lib/firebase";
import { env } from "@/config/env";

// Components (local)
import { AuthModeToggle } from "./AuthModeToggle";
import { EmailVerificationDialog } from "./EmailVerificationDialog";
import { PasswordRequirement } from "./PasswordRequirement";

// Constants & Types
import { StorageKeys } from "../../constants";
import type { AuthComponentProps, AuthMode } from "./types";

// Assets
import googleLogoUrl from "../icons/google_logo.svg";

type FieldErrors = Record<string, string>;

export function AuthComponent({
  initialMode = "login",
  onSuccess,
  hideGoogle,
  initialErrorKey,
  className,
  "data-testid": dataTestId,
}: AuthComponentProps) {
  const { signInWithEmail, signInWithGoogle, signUpWithEmail, authStatus } =
    useAuth();
  const t = useTranslation();

  const [mode, setMode] = useState<AuthMode>(initialMode);

  const isLogin = mode === "login";
  const isRegister = mode === "register";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [accountType, setAccountType] = useState<RegisterAccountType>("user");
  const [orgName, setOrgName] = useState("");

  const [errorKey, setErrorKey] = useState(initialErrorKey ?? "");
  const [verifyShown, setVerifyShown] = useState(false);
  const [verifyEmailTo, setVerifyEmailTo] = useState("");

  const error = useMemo(() => {
    if (!errorKey) return "";
    return t(errorKey as keyof typeof t);
  }, [errorKey, t]);
  const [loading, setLoading] = useState(false);

  // Smooth HORIZONTAL slider (no peeking):
  // - both panels rendered and absolutely positioned
  // - container clips overflow
  // - container height animates to the ACTIVE panel height
  const loginPanelRef = useRef<HTMLDivElement>(null);
  const registerPanelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);

  const measureHeights = () => {
    const hLogin = loginPanelRef.current?.offsetHeight ?? 0;
    const hReg = registerPanelRef.current?.offsetHeight ?? 0;

    const next = mode === "login" ? hLogin : hReg;
    if (next > 0) setPanelHeight(next);
  };

  useLayoutEffect(() => {
    measureHeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountType, mode]);

  useEffect(() => {
    const id = window.setTimeout(measureHeights, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, accountType, error, fieldErrors, t]);

  useEffect(() => {
    // If auth provider forces registration state, ensure this component switches.
    if (authStatus === "needs_registration") {
      resetRegistrationState();
      setMode("register");
      setErrorKey(initialErrorKey ?? "registrationRequired");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus]);

  const apiBaseUrl = env.apiBaseUrl;

  const resetAllState = () => {
    setMode("login");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setAccountType("user");
    setOrgName("");
    setFieldErrors({});
    setErrorKey(initialErrorKey ?? "");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const resetRegistrationState = () => {
    setFirstName("");
    setLastName("");
    setAccountType("user");
    setOrgName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFieldErrors({});
    setErrorKey(initialErrorKey ?? "");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const isEmail = (v: string): boolean => {
    const value = v.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const looksLikePhone = (v: string): boolean => {
    const value = v.trim();
    return /^[0-9\-+()\s]+$/.test(value);
  };

  const passwordRequirements = useMemo(() => {
    const v = password;
    return {
      minLen: v.length >= 8,
      number: /\d/.test(v),
      symbol: /[^A-Za-z0-9]/.test(v),
      upper: /[A-Z]/.test(v),
      lower: /[a-z]/.test(v),
    };
  }, [password]);

  const validatePassword = (): string | null => {
    if (!passwordRequirements.minLen) return "passwordMin8";
    if (!passwordRequirements.number) return "passwordNeedNumber";
    if (!passwordRequirements.symbol) return "passwordNeedSymbol";
    if (!passwordRequirements.upper) return "passwordNeedUpper";
    if (!passwordRequirements.lower) return "passwordNeedLower";
    return null;
  };

  const validateLogin = (): boolean => {
    const errs: FieldErrors = {};

    if (!email.trim()) errs.email = "emailRequired";
    else if (looksLikePhone(email)) errs.email = "emailInvalid";
    else if (!isEmail(email)) errs.email = "emailInvalid";

    if (!password) errs.password = "passwordRequired"; // pragma: allowlist secret

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateRegister = (): boolean => {
    const errs: FieldErrors = {};

    if (!firstName.trim()) errs.firstName = "firstNameRequired";
    if (!lastName.trim()) errs.lastName = "lastNameRequired";

    // If the user is already authenticated in Firebase (e.g. Google sign-in),
    // the backend uses the ID token identity; we only need profile fields.
    const hasFirebaseUser = Boolean(auth.currentUser);

    if (!hasFirebaseUser) {
      if (!email.trim()) errs.email = "emailRequired";
      else if (looksLikePhone(email)) errs.email = "emailInvalid";
      else if (!isEmail(email)) errs.email = "emailInvalid";

      const pwError = validatePassword();
      if (pwError) errs.password = pwError; // pragma: allowlist secret

      if (!confirmPassword)
        errs.confirmPassword = "confirmPasswordRequired"; // pragma: allowlist secret
      else if (confirmPassword !== password)
        errs.confirmPassword = "passwordsDontMatch"; // pragma: allowlist secret
    }

    if (accountType === "partner" && !orgName.trim()) {
      errs.orgName = "orgNameRequired";
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const clearErrorsOnModeSwitch = async (nextMode: AuthMode) => {
    setErrorKey("");
    setFieldErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);

    if (nextMode === "login") {
      // If we were in a "needs_registration" state, Firebase may still have a currentUser.
      try {
        await firebaseSignOut(auth);
      } catch {
        // ignore
      }
      try {
        window.sessionStorage.removeItem(StorageKeys.pendingRegistration);
      } catch {
        // ignore
      }
      resetAllState();
      return;
    }

    resetRegistrationState();
    setMode("register");
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLogin()) {
      setErrorKey("");
      return;
    }

    setErrorKey("");
    setLoading(true);
    try {
      await signInWithEmail(email, password);
      onSuccess?.();
    } catch (err) {
      let errorCode: string | undefined;
      if (typeof err === "object" && err && "code" in err) {
        errorCode = (err as { code?: string }).code;
      }
      if (errorCode === "auth/user-not-found") {
        setErrorKey("userNotFound");
      } else if (errorCode === "auth/wrong-password") {
        setErrorKey("wrongPassword");
      } else {
        const message =
          typeof err === "object" && err && "message" in err
            ? String((err as { message?: string }).message)
            : "";
        if (message.includes("not authorized")) {
          setErrorKey("notAuthorized");
        } else {
          setErrorKey("authFailed");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setErrorKey("");
    setLoading(true);
    try {
      await signInWithGoogle();
      onSuccess?.();
    } catch (err) {
      const errorCode =
        typeof err === "object" && err && "code" in err
          ? String((err as { code?: string }).code)
          : "";

      if (errorCode === "auth/popup-closed-by-user") {
        setErrorKey("signInCancelled");
        return;
      }

      const message =
        typeof err === "object" && err && "message" in err
          ? String((err as { message?: string }).message)
          : "";

      if (message.includes("not authorized")) {
        setErrorKey("notAuthorized");
      } else {
        setErrorKey("googleSignInFailed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateRegister()) {
      setErrorKey("");
      return;
    }

    setErrorKey("");
    setLoading(true);

    try {
      if (!auth.currentUser) {
        await signUpWithEmail(email, password);
      }

      const u = auth.currentUser;
      if (!u) {
        throw new Error("NO_FIREBASE_USER");
      }

      const token = await u.getIdToken();

      await callAuthRegister(apiBaseUrl, token, {
        accountType,
        firstName,
        lastName,
        orgName: accountType === "partner" ? orgName : null,
      });

      try {
        window.sessionStorage.removeItem(StorageKeys.pendingRegistration);
      } catch {
        // ignore
      }

      // If this is a password signup, send verification email and show dialog.
      const providers = (u.providerData || []).map((p) => p?.providerId);
      if (providers.includes("password")) {
        try {
          // Always use backend API to send verification emails via Resend
          // (or logs link in dev/staging if Resend not configured)
          await callSendEmailVerification(apiBaseUrl, token);
        } catch {
          // best-effort; continue even if send fails
        }
        setVerifyEmailTo(email);
        setVerifyShown(true);
        try {
          await firebaseSignOut(auth);
        } catch {
          // ignore
        }
        return;
      }

      await u.getIdToken(true);
      // For Google registration we can reload to re-authorize.
      window.location.reload();
    } catch (err) {
      const code =
        typeof err === "object" && err && "code" in err
          ? String((err as { code: unknown }).code)
          : "";
      if (code === "ORG_NAME_REQUIRED") {
        setErrorKey("fillAllFields");
      } else {
        setErrorKey("registrationFailed");
      }
    } finally {
      setLoading(false);
    }
  };

  const loginPanelClass =
    mode === "login"
      ? "translate-x-0 opacity-100 pointer-events-auto"
      : "-translate-x-full opacity-0 pointer-events-none";

  const registerPanelClass =
    mode === "register"
      ? "translate-x-0 opacity-100 pointer-events-auto"
      : "translate-x-full opacity-0 pointer-events-none";

  const handleModeChange = (newMode: AuthMode) => {
    void clearErrorsOnModeSwitch(newMode);
  };

  const handleVerificationDialogClose = () => {
    setVerifyShown(false);
    void clearErrorsOnModeSwitch("login");
  };

  return (
    <div className={className} data-testid={dataTestId}>
      {verifyShown && (
        <EmailVerificationDialog
          email={verifyEmailTo}
          onClose={handleVerificationDialogClose}
        />
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm mt-6">
          {error}
        </div>
      )}

      <AuthModeToggle mode={mode} onModeChange={handleModeChange} />

      <div
        className="relative mt-6 overflow-hidden transition-[height] duration-500 ease-in-out px-3"
        style={panelHeight ? { height: panelHeight } : undefined}
      >
        {/* LOGIN PANEL */}
        <div
          className={`absolute inset-0 transition-all duration-500 ease-in-out will-change-transform ${loginPanelClass}`}
          aria-hidden={mode !== "login"}
        >
          <div ref={loginPanelRef}>
            <form onSubmit={handleLoginSubmit} className="space-y-6" noValidate>
              <fieldset>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="login_email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("email")}
                    </label>
                    <input
                      id="login_email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (fieldErrors.email) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.email;
                            return next;
                          });
                        }
                      }}
                      className={`input-field mt-1 ${isLogin && fieldErrors.email ? "border-red-500 focus:ring-red-500" : ""}`}
                    />
                    {isLogin && fieldErrors.email && (
                      <p className="mt-1 text-xs text-red-600">
                        {t(fieldErrors.email as keyof typeof t)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="login_password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("password")}
                    </label>
                    <div className="relative mt-1">
                      <input
                        id="login_password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (fieldErrors.password) {
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next.password;
                              return next;
                            });
                          }
                        }}
                        className={`input-field pr-12 ${isLogin && fieldErrors.password ? "border-red-500 focus:ring-red-500" : ""}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-2 my-auto p-1 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    {isLogin && fieldErrors.password && (
                      <p className="mt-1 text-xs text-red-600">
                        {t(fieldErrors.password as keyof typeof t)}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary bg-red-600 hover:bg-red-700 text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                >
                  {loading ? t("pleaseWait") : t("signIn")}
                </button>
              </fieldset>
            </form>

            {!hideGoogle && (
              <>
                <div className="my-6 border-t border-gray-200" />

                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 bg-white"
                >
                  {!loading && (
                    <img src={googleLogoUrl} alt="Google" className="w-5 h-5" />
                  )}
                  <span className="text-gray-700">
                    {loading ? t("pleaseWait") : t("continueWithGoogle")}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* REGISTER PANEL */}
        <div
          className={`absolute inset-0 transition-all duration-500 ease-in-out will-change-transform ${registerPanelClass}`}
          aria-hidden={mode !== "register"}
        >
          <div ref={registerPanelRef}>
            <form
              onSubmit={handleRegisterSubmit}
              className="space-y-6"
              noValidate
            >
              <fieldset>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="first_name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("firstName")}
                    </label>
                    <input
                      id="first_name"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (fieldErrors.firstName) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.firstName;
                            return next;
                          });
                        }
                      }}
                      className={`input-field mt-1 ${fieldErrors.firstName ? "border-red-500 focus:ring-red-500" : ""}`}
                    />
                    {fieldErrors.firstName && (
                      <p className="mt-1 text-xs text-red-600">
                        {t(fieldErrors.firstName as keyof typeof t)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="last_name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("lastName")}
                    </label>
                    <input
                      id="last_name"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (fieldErrors.lastName) {
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.lastName;
                            return next;
                          });
                        }
                      }}
                      className={`input-field mt-1 ${fieldErrors.lastName ? "border-red-500 focus:ring-red-500" : ""}`}
                    />
                    {fieldErrors.lastName && (
                      <p className="mt-1 text-xs text-red-600">
                        {t(fieldErrors.lastName as keyof typeof t)}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="block text-sm font-medium text-gray-700">
                      {t("accountType")}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setAccountType("user")}
                        className={`text-left rounded-lg p-3 transition-colors border focus:outline-none ${
                          accountType === "user"
                            ? "border-2 border-red-500"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {t("accountTypeUser")}
                            </div>
                            <div className="mt-1 text-xs text-gray-600">
                              {t("accountTypeUserHint")}
                            </div>
                          </div>
                          <span
                            className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${
                              accountType === "user"
                                ? "border-red-600"
                                : "border-gray-300"
                            }`}
                          >
                            {accountType === "user" && (
                              <span className="block h-full w-full rounded-full bg-red-600 scale-[0.55]" />
                            )}
                          </span>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setAccountType("partner")}
                        className={`text-left rounded-lg p-3 transition-colors border focus:outline-none ${
                          accountType === "partner"
                            ? "border-2 border-red-500"
                            : "border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium text-gray-900">
                              {t("accountTypePartner")}
                            </div>
                            <div className="mt-1 text-xs text-gray-600">
                              {t("accountTypePartnerHint")}
                            </div>
                          </div>
                          <span
                            className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${
                              accountType === "partner"
                                ? "border-red-600"
                                : "border-gray-300"
                            }`}
                          >
                            {accountType === "partner" && (
                              <span className="block h-full w-full rounded-full bg-red-600 scale-[0.55]" />
                            )}
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {accountType === "partner" && (
                    <div>
                      <label
                        htmlFor="org_name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t("orgName")}
                      </label>
                      <input
                        id="org_name"
                        type="text"
                        required
                        value={orgName}
                        onChange={(e) => {
                          setOrgName(e.target.value);
                          if (fieldErrors.orgName) {
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next.orgName;
                              return next;
                            });
                          }
                        }}
                        className={`input-field mt-1 ${fieldErrors.orgName ? "border-red-500 focus:ring-red-500" : ""}`}
                      />
                      {fieldErrors.orgName && (
                        <p className="mt-1 text-xs text-red-600">
                          {t(fieldErrors.orgName as keyof typeof t)}
                        </p>
                      )}
                    </div>
                  )}

                  {!auth.currentUser && (
                    <>
                      <div>
                        <label
                          htmlFor="reg_email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t("email")}
                        </label>
                        <input
                          id="reg_email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (fieldErrors.email) {
                              setFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.email;
                                return next;
                              });
                            }
                          }}
                          className={`input-field mt-1 ${isRegister && fieldErrors.email ? "border-red-500 focus:ring-red-500" : ""}`}
                        />
                        {isRegister && fieldErrors.email && (
                          <p className="mt-1 text-xs text-red-600">
                            {t(fieldErrors.email as keyof typeof t)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="reg_password"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t("password")}
                        </label>
                        <div className="relative mt-1">
                          <input
                            id="reg_password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            required
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              if (fieldErrors.password) {
                                setFieldErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.password;
                                  return next;
                                });
                              }
                            }}
                            className={`input-field pr-12 ${isRegister && fieldErrors.password ? "border-red-500 focus:ring-red-500" : ""}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute inset-y-0 right-2 my-auto p-1 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        </div>

                        <div
                          className="mt-2 grid grid-cols-1 gap-1"
                          aria-label={t("passwordRequirements")}
                        >
                          <PasswordRequirement
                            ok={passwordRequirements.minLen}
                            text={t("passwordReqMin8")}
                          />
                          <PasswordRequirement
                            ok={passwordRequirements.number}
                            text={t("passwordReqNumber")}
                          />
                          <PasswordRequirement
                            ok={passwordRequirements.symbol}
                            text={t("passwordReqSymbol")}
                          />
                          <PasswordRequirement
                            ok={passwordRequirements.upper}
                            text={t("passwordReqUpper")}
                          />
                          <PasswordRequirement
                            ok={passwordRequirements.lower}
                            text={t("passwordReqLower")}
                          />
                        </div>

                        {isRegister && fieldErrors.password && (
                          <p className="mt-1 text-xs text-red-600">
                            {t(fieldErrors.password as keyof typeof t)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="reg_confirm_password"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t("confirmPassword")}
                        </label>
                        <div className="relative mt-1">
                          <input
                            id="reg_confirm_password"
                            type={showConfirmPassword ? "text" : "password"}
                            autoComplete="new-password"
                            required
                            value={confirmPassword}
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                              if (fieldErrors.confirmPassword) {
                                setFieldErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.confirmPassword;
                                  return next;
                                });
                              }
                            }}
                            className={`input-field pr-12 ${isRegister && fieldErrors.confirmPassword ? "border-red-500 focus:ring-red-500" : ""}`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            className="absolute inset-y-0 right-2 my-auto p-1 text-gray-500 hover:text-gray-700"
                          >
                            {showConfirmPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        </div>
                        {isRegister && fieldErrors.confirmPassword && (
                          <p className="mt-1 text-xs text-red-600">
                            {t(fieldErrors.confirmPassword as keyof typeof t)}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary bg-red-600 hover:bg-red-700 text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                >
                  {loading ? t("pleaseWait") : t("createAccount")}
                </button>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
